import { hasIxigoHistoryMetaData, type IxigoHistoryMetaData } from './utils';

/**
 * Native-declared back-intent stamped on the entry-page `history.state`.
 * Extensible enum; absence remains the fallback default. See
 * `apps/web/docs/adr/0001-native-webview-back-intent.md`.
 */
export type OnExitIntent = 'quit';

export type LastHistoryState = IxigoHistoryMetaData & {
	/**
	 * this gets stored in session storage every time a new entry is created
	 * length of history at current point
	 */
	total: number;

	/**
	 * url snapshot of the last history entry
	 */
	url: string;
};
const sessionKey = '_last_history_state';
function getLastHistoryState() {
	const state = sessionStorage?.getItem(sessionKey);
	const parsedState = state ? JSON.parse(state) : null;
	// check if it's a valid LastHistoryState then return it
	if (
		parsedState?.total &&
		parsedState?.url &&
		hasIxigoHistoryMetaData(parsedState)
	) {
		return parsedState as LastHistoryState;
	}
	return null;
}
function setLastHistoryState(
	ixiMeta: IxigoHistoryMetaData['ixiMeta'],
	total = history.length,
	url = window.location.href,
) {
	const state = JSON.stringify({ ixiMeta, total, url });
	sessionStorage?.setItem(sessionKey, state);
}
// user enters x.com
// idx: 0, aidx: 1, history.length: 2
// pushes a new entry with different pathname (x.com/second)
// idx: 1, aidx: 1, history.length: 3
// pushes a new entry with same pathname (x.com/second?f=1)
// idx: 2, aidx: 2, history.length: 4 - // to go to previous different url from current url, user has to go back 2 times (history.go(-state.idx))
// last history state is stored in session storage - { idx: 2, aidx: 2, total: 4, url: 'x.com/second?f=1' }

// NOTE: following scenario is possible to implement
// but the new navigation api doesn't preserve old entries once you move to different domain
// and come back agin by creating a new entry instead of backward navigation
// so we'll also not handle this scenario
// pushes a new entry with different domain y.com where we don't modify history
// history.length: 5
// pushes a new entry on this domain
// history.length: 6

// here now there could be two scenarios
// 1. user moves to x.com/third
// history.length: 7 - (idx: 3, aidx: 3(history.length - lastHistoryState.total)

// 2. user moves to y.com/second?*
// history.length: 7 - (idx: 3, aidx: lastEntry.ixiMeta.aidx + (history.length - lastHistoryState.total))

// for simiplification we'll only handle the case where current history length is one more than the total value stored in last history state
// and referer url is same domain or not present
// otherwise we'll just start with idx: 0, aidx: 1

export function patchHistory() {
	const originalPushState = history.pushState;
	const originalReplaceState = history.replaceState;
	const lastEntry = getLastHistoryState();

	const entryUrl = new URL(window.location.href);
	const onExitParam = entryUrl.searchParams.get('onExit');
	const onExitIntent: OnExitIntent | undefined =
		onExitParam === 'quit' ? 'quit' : undefined;

	// if it doesn't have ixiMeta or the url has changed possibly using location.replace which reloads the page as well
	if (!hasIxigoHistoryMetaData(history.state)) {
		// this url is first url for the domain in history
		// this is not entirely true for the scenario where user navigates to a different domain
		// and then comes back by entering the url or third party website redirects to our website

		let ixiMeta: IxigoHistoryMetaData['ixiMeta'] = {
			idx: 0,
			aidx: 1,
		};
		if (lastEntry) {
			// history length doesn't change in case where users navigates from page 1 to page 2
			// goes back to page 1 using history back and then goes again to page 2
			// in this case history length is same as last history state
			// in some scenario after pushing history, total history length might reduce as well
			const delta = history.length - lastEntry.total;
			const lastPathname = new URL(lastEntry.url, window.location.origin)
				.pathname;
			const currentPathname = location.pathname;
			if (delta === 1 || delta < 0) {
				ixiMeta = {
					idx: lastEntry.ixiMeta.idx + 1,
					aidx:
						lastPathname === currentPathname
							? (lastEntry.ixiMeta.aidx || 0) + 1
							: 1,
				};
			} else if (delta === 0) {
				// No new entry added (e.g., location.replace). Do not increment idx.
				ixiMeta = {
					idx: lastEntry.ixiMeta.idx,
					aidx:
						lastPathname === currentPathname ? lastEntry.ixiMeta.aidx || 1 : 1,
				};
			}
		}
		const newState: Record<string, unknown> = { ...history.state, ixiMeta };
		if (onExitIntent) newState.onExit = onExitIntent;
		history.replaceState(newState, document.title || '');
		setLastHistoryState(ixiMeta);
	} else if (
		lastEntry &&
		lastEntry.url !== location.href &&
		lastEntry.ixiMeta.idx === history.state.ixiMeta.idx
	) {
		// if the url has changed possibly using location.replace which reloads the page as well
		// it means going back 1 step will take use to the last url which is different from the current url
		lastEntry.ixiMeta.aidx = 1;
		history.replaceState(
			{ ...history.state, ixiMeta: lastEntry.ixiMeta },
			document.title || '',
		);
		setLastHistoryState(lastEntry.ixiMeta);
	}

	if (entryUrl.searchParams.has('onExit')) {
		entryUrl.searchParams.delete('onExit');
		originalReplaceState.call(
			history,
			history.state,
			document.title || '',
			entryUrl.pathname + entryUrl.search + entryUrl.hash,
		);
	}

	history.pushState = function (
		state: unknown,
		title: string,
		url?: string | null,
	) {
		const newUrl = url ? new URL(url, window.location.origin) : location;
		const newPathname = url ? newUrl.pathname : location.pathname;
		const currentPathname = location.pathname;
		const currentState = hasIxigoHistoryMetaData(history.state)
			? history.state.ixiMeta
			: { idx: -1, aidx: 0 };
		const ixiMeta = {
			idx: currentState.idx + 1,
			aidx: newPathname === currentPathname ? currentState.aidx + 1 : 1,
		};
		const newState = {
			...(state || {}),
			ixiMeta,
		};
		originalPushState.call(this, newState, title, url);
		// it is after calling originalPushState because history length might increase, decrease or remain same
		setLastHistoryState(ixiMeta);
	};
	history.replaceState = function (
		state: unknown,
		title: string,
		url?: string | null,
	) {
		const newUrl = url ? new URL(url, window.location.origin) : location;
		const newPathname = url ? newUrl.pathname : location.pathname;
		const currentPathname = location.pathname;
		const newState = { ...history.state, ...(state || {}) };

		// this might break in this scenario
		// user lands to /flights
		// then user navigates to /flight-status
		// now replacing current url with /flights will result in history entry with duplicate path which is not accounted in aidx
		// in this case after replacing with /flights, aidx should be 2 to account for the duplicate entry

		// if new path is different from current pathname, reset aidx to 1
		if (hasIxigoHistoryMetaData(newState) && newPathname !== currentPathname) {
			newState.ixiMeta.aidx = 1;
		}
		originalReplaceState.call(this, newState, title, url);
		setLastHistoryState(newState.ixiMeta, history.length, newUrl.href);
	};
}
